import { t } from "@lingui/macro";
import {
  useRoute,
  RouteProp,
  useNavigation,
  NavigationProp,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtom, useAtomValue } from "jotai";
import { Suspense, useEffect, useCallback, useMemo, useState } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";

import { AuthenticateBeforeProceedScreen } from "@/features/Authentication/components/AuthenticateBeforeProceedScreen/AuthenticateBeforeProceedScreen";
import { BookingInfo, BookingPolicy } from "@/features/BookingParts";
import { RecurringRequestPreviewProvider } from "@/features/RecurringRequests/RecurringRequestPreviewProvider";
import { RecurrenceAvailabilityPreview } from "@/features/RecurringRequests/types";
import { ClosedRequestWindowBanner } from "@/screens/BookingDetail/components/ClosedRequestWindowBanner";
import { useFlags } from "@/utils/launchDarkly/useFlags";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { Box, HStack } from "@whoosh/design-system/src/components/primitive";

import { NotificationsNudgeModal } from "../../../../../features/Session/components/NotificationsNudgeModal/NotificationsNudgeModal";
import { useCurrentUserHasSeenNotificationNudgeModal } from "../../../../../features/Session/hooks/useCurrentUserHasSeenNotificationNudgeModal";
import { ClubStackNavigatorParamList } from "../../../../../navigators/ClubNavigator";
import { HomeTabsNavigatorParamList } from "../../../../../navigators/HomeTabsNavigator/types";
import { BookingConfirmedModal } from "../BookingConfirmedModal/BookingConfirmedModal";
import {
  DiningBookingSummary,
  DiningBookingSummaryQueryString,
} from "../DiningBookingSummary/DiningBookingSummary";
import { DiningBookingSummaryQuery } from "../DiningBookingSummary/__generated__/DiningBookingSummaryQuery.graphql";
import { BookingSummaryMultipleView } from "./BookingSummaryMultipleView";
import {
  BookingSummaryQuery,
  BookingType,
} from "./__generated__/BookingSummaryQuery.graphql";
import { BookingSummarySessionQuery } from "./__generated__/BookingSummarySessionQuery.graphql";
import { BookingSummary_booking$key } from "./__generated__/BookingSummary_booking.graphql";
import { useInitializeBookingSummaryAnalyticsDataAtom } from "./atoms/analytics/useInitializeBookingSummaryAnalyticsDataAtom";
import {
  confirmedBookingModalTypeAtom,
  isConfirmedBookingModalOpenAtom,
  isNotificationsNudgeModalOpenAtom,
} from "./atoms/stores";
import { getBookingSummaryDisplayConfig } from "./bookingSummaryDisplayConfig";
import {
  BOOKING_SUMMARY_CONTENT_MAX_WIDTH,
  useIsBookingSummaryNarrow,
  useIsBookingSummaryStacked,
} from "./bookingSummaryWidth";
import { BookingCreditSelectModals } from "./components/BookingCreditSelectModals/BookingCreditSelectModals";
import { BookingDetailsView } from "./components/BookingDetailsView/BookingDetailsView";
import { BookingHeader } from "./components/BookingHeader/BookingHeader";
import { BookingPayment } from "./components/BookingPayment/BookingPayment";
import { AvailableTeeTimes } from "./components/BookingRequest/components/AvailableTeeTimes/AvailableTeeTimes";
import {
  useIsRequestConfigRequestWindowClosed,
  useShouldPresentBookingAsLockedRequest,
} from "./components/BookingRequest/components/AvailableTeeTimes/hooks/useShouldPresentBookingAsLockedRequest";
import { RequestInfoBanner } from "./components/BookingRequest/components/RequestInfoBanner/RequestInfoBanner";
import { BookingReservation } from "./components/BookingReservation/BookingReservation";
import { BookingSummaryLayout } from "./components/BookingSummaryLayout/BookingSummaryLayout";
import { BookingSummarySkeleton } from "./components/BookingSummarySkeleton/BookingSummarySkeleton";
import { ChargesSummary } from "./components/ChargesSummary/ChargesSummary";
import { InviteOthersBox } from "./components/InviteOthersBox/InviteOthersBox";
import { LessonInfoBanner } from "./components/LessonInfoBanner/LessonInfoBanner";
import { MemberNote } from "./components/MemberNote/MemberNote";
import { PaymentCardEntryModals } from "./components/PaymentCardEntryModals/PaymentCardEntryModals";
import { RemoteLockCode } from "./components/RemoteLockCode/RemoteLockCode";
import { useInitializePunchPassesFromBooking } from "./hooks/useInitializePunchPassesFromBooking";
import { useResetConfirmedBookingModalOnLeave } from "./hooks/useResetConfirmedBookingModalOnLeave";
import { getRequestGroupCount } from "./utils/getRequestGroupCount";

export const BookingSummary = () => {
  const route =
    useRoute<RouteProp<ClubStackNavigatorParamList, "BookingSummary">>();
  useResetConfirmedBookingModalOnLeave(route.params.slotOrBookingId);

  const [queryRef, loadQuery] = useQueryLoader<BookingSummarySessionQuery>(
    BookingSummarySessionQueryString
  );

  useEffect(() => {
    loadQuery({}, { fetchPolicy: "store-and-network" });
  }, [loadQuery]);

  return (
    <RecurringRequestPreviewProvider>
      <Suspense fallback={null}>
        {queryRef ? <BookingSummaryScreen queryRef={queryRef} /> : null}
      </Suspense>
    </RecurringRequestPreviewProvider>
  );
};
type BookingSummaryScreenProps = {
  queryRef: PreloadedQuery<BookingSummarySessionQuery>;
};
const BookingSummaryScreen = ({ queryRef }: BookingSummaryScreenProps) => {
  const data = usePreloadedQuery(BookingSummarySessionQueryString, queryRef);
  const { patronLoyalty } = useFlags();
  const clubSupportsPrivateBookings =
    data.session?.currentClientProfile?.member?.club?.supportsPrivate ?? false;
  const membershipTypeId =
    data.session?.currentClientProfile?.member?.membershipType?.id;
  const currentSessionUserClientId =
    data.session?.currentClientProfile?.member?.id ?? "";
  const [summaryRef, loadQuery] = useQueryLoader<BookingSummaryQuery>(
    BookingSummaryQueryString
  );

  const [diningSummaryRef, loadDiningSummaryQuery] =
    useQueryLoader<DiningBookingSummaryQuery>(DiningBookingSummaryQueryString);

  const route =
    useRoute<RouteProp<ClubStackNavigatorParamList, "BookingSummary">>();

  const { slotOrBookingId } = route.params;
  const bookingType: BookingType = useMemo(
    () =>
      slotOrBookingId.includes("LessonBooking")
        ? "LESSON"
        : slotOrBookingId.includes("DiningBooking")
        ? "DINING"
        : "STANDARD",
    [slotOrBookingId]
  );
  const isDiningBooking = bookingType === "DINING";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleFetchBooking = useCallback(() => {
    if (!membershipTypeId) return;

    if (isDiningBooking) {
      //Load Dining Booking Summary Query Here
      loadDiningSummaryQuery(
        { bookingId: slotOrBookingId },
        { fetchPolicy: "store-and-network" }
      );
    } else {
      loadQuery(
        {
          bookingId: slotOrBookingId,
          membershipTypeId: membershipTypeId,
          bookingType: bookingType,
          patronLoyalty: !!patronLoyalty,
        },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [
    bookingType,
    isDiningBooking,
    loadDiningSummaryQuery,
    loadQuery,
    membershipTypeId,
    patronLoyalty,
    slotOrBookingId,
  ]);

  useEffect(() => {
    handleFetchBooking();
  }, [handleFetchBooking]);

  const isLessonBooking = slotOrBookingId.includes("LessonBooking");

  const navigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();
  const handleGoBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate("MainHome");
    }
  }, [navigation]);
  if (
    route.params.slotOrBookingId === "multiple-tee-times" &&
    membershipTypeId
  ) {
    return (
      <BookingSummaryMultipleView
        currentSessionMemberShipTypeId={membershipTypeId}
        currentSessionUserClientId={currentSessionUserClientId}
        sessionFragmentRef={data.session}
        supportsPrivateBookings={clubSupportsPrivateBookings}
        isLessonBooking={isLessonBooking}
      />
    );
  }

  return (
    <AuthenticateBeforeProceedScreen
      onAuthenticationSuccess={handleFetchBooking}
    >
      <Suspense
        fallback={
          <BookingSummarySkeleton
            supportsPrivateBookings={clubSupportsPrivateBookings}
            isLessonBooking={isLessonBooking}
            clubSupportsPrivateBookings={clubSupportsPrivateBookings}
          />
        }
      >
        {summaryRef && membershipTypeId && !isDiningBooking ? (
          <BookingSummaryQueryRenderer
            queryRef={summaryRef}
            currentSessionMemberShipTypeId={membershipTypeId}
            currentSessionUserClientId={currentSessionUserClientId}
            handleGoBack={handleGoBack}
          />
        ) : isDiningBooking && diningSummaryRef ? (
          <DiningBookingSummary
            diningSummaryRef={diningSummaryRef}
            handleGoBack={handleGoBack}
          />
        ) : null}
      </Suspense>
    </AuthenticateBeforeProceedScreen>
  );
};

type Props = {
  queryRef: PreloadedQuery<BookingSummaryQuery>;
  currentSessionMemberShipTypeId: string;
  currentSessionUserClientId: string;
  handleGoBack: () => void;
};
export const BookingSummaryQueryRenderer = ({
  queryRef,
  currentSessionMemberShipTypeId,
  currentSessionUserClientId,
  handleGoBack,
}: Props) => {
  const data = usePreloadedQuery(BookingSummaryQueryString, queryRef);

  const requestGroupCount = getRequestGroupCount(
    data.node?.rounds?.edges?.map((edge) => edge?.node)
  );
  const requestRoundBookingIndexes =
    data.node?.rounds?.edges?.map((edge) => edge?.node?.bookingIndex) ?? [];

  return (
    <BookingSummaryQueryRendererContent
      data={data}
      requestGroupCount={requestGroupCount}
      requestRoundBookingIndexes={requestRoundBookingIndexes}
      hasAvailableMultipleSlotRequestSequence={
        data.node?.hasFullfillableMultipleSlotRequestSequence ?? false
      }
      currentSessionMemberShipTypeId={currentSessionMemberShipTypeId}
      currentSessionUserClientId={currentSessionUserClientId}
      handleGoBack={handleGoBack}
    />
  );
};

type BookingSummaryQueryRendererContentProps = Omit<Props, "queryRef"> & {
  data: BookingSummaryQuery["response"];
  requestGroupCount: number;
  hasAvailableMultipleSlotRequestSequence: boolean;
  requestRoundBookingIndexes?: readonly (number | null | undefined)[];
};

const BookingSummaryQueryRendererContent = ({
  data,
  requestGroupCount,
  hasAvailableMultipleSlotRequestSequence,
  requestRoundBookingIndexes,
  currentSessionMemberShipTypeId,
  currentSessionUserClientId,
  handleGoBack,
}: BookingSummaryQueryRendererContentProps) => {
  const isNarrow = useIsBookingSummaryNarrow();
  const isStacked = useIsBookingSummaryStacked();
  const insets = useSafeAreaInsets();
  const route =
    useRoute<RouteProp<ClubStackNavigatorParamList, "BookingSummary">>();
  const pendingRecurrence = route.params?.params?.pendingRecurrence ?? null;
  const [recurrenceAvailabilityPreview, setRecurrenceAvailabilityPreview] =
    useState<RecurrenceAvailabilityPreview | null>(null);
  const booking = useFragment<BookingSummary_booking$key>(
    BookingSummaryBookingFragmentString,
    data.node
  );
  const isAggregateRequest =
    requestGroupCount > 1 && booking?.startSlot?.__typename === "QueueSlot";
  const effectiveRequestGroupCount = isAggregateRequest ? requestGroupCount : 1;

  useInitializePunchPassesFromBooking({
    fragmentRef: booking ?? undefined,
  });
  useInitializeBookingSummaryAnalyticsDataAtom(booking ?? null);
  const hasPublicSeenNotificationNudgeModal =
    useCurrentUserHasSeenNotificationNudgeModal(data.session ?? null);
  const session = data.session;
  const clubSlug = data.session?.currentClientProfile?.member?.club?.slug;
  const isMemberCaller =
    data.session?.currentClientProfile?.member?.__typename === "Member";

  const currentUserClientId = session?.currentClientProfile?.member?.id ?? null;
  const ownerId = booking?.ownerClient?.id ?? null;
  const isBookingOwner =
    currentUserClientId && ownerId && currentUserClientId === ownerId;

  const [isNotificationsNudgeModalOpen, setIsNotificationsNudgeModalOpen] =
    useAtom(isNotificationsNudgeModalOpenAtom);

  const [isConfirmedBookingModalOpen, setIsConfirmedBookingModalOpen] = useAtom(
    isConfirmedBookingModalOpenAtom
  );
  const confirmedBookingModalType = useAtomValue(confirmedBookingModalTypeAtom);

  const clubSupportsPrivateBookings =
    booking?.startSlot?.agenda?.facility?.club?.supportsPrivate;

  const isLessonBooking = booking?.__typename === "LessonBooking";

  const isPrivateLessonRequest =
    (isLessonBooking &&
      booking?.startSlot?.agenda?.facility?.privateLessonsPermissionSet
        ?.patronAccess === "REQUEST" &&
      clubSupportsPrivateBookings) ??
    false;

  const { showToast } = useToast();
  const {
    shouldShowChargesSummary,
    shouldShowBookingPayment,
    shouldShowFooter,
  } = getBookingSummaryDisplayConfig({
    isMobile: isStacked,
    clubSupportsPrivateBookings: Boolean(clubSupportsPrivateBookings),
    isPrivateLessonRequest,
    isLessonBooking,
  });
  const shouldPresentAsRequest = useShouldPresentBookingAsLockedRequest(
    booking ?? null
  );
  const isClosedRequestWindow = useIsRequestConfigRequestWindowClosed(
    booking ?? null
  );

  //If we get no booking -- we just bail and go back.
  useEffect(() => {
    if (!booking) {
      showToast({
        title: t`Booking not found`,
        description: t`Sorry about that but I couldn't find that booking.`,
        semanticStyle: "error",
        toastId: "booking-not-found-error-toast",
      });
      handleGoBack();
    }
  }, [booking, handleGoBack, showToast]);

  return (
    <HStack
      width="100%"
      flex={1}
      style={{ paddingTop: insets.top, paddingBottom: insets.bottom }}
      backgroundColor="surface-secondary"
    >
      {!isStacked ? (
        <Box
          backgroundColor={
            clubSupportsPrivateBookings
              ? "surface-secondary"
              : "surface-primary"
          }
          flexGrow={1}
          flexShrink={1}
          flexBasis={0}
        />
      ) : null}
      <Box
        backgroundColor={
          clubSupportsPrivateBookings ? "surface-secondary" : "surface-primary"
        }
        flexShrink={1}
        flexGrow={1}
      >
        <Box
          maxWidth={BOOKING_SUMMARY_CONTENT_MAX_WIDTH}
          marginLeft="auto"
          height="100%"
          width="100%"
          marginTop={isNarrow ? 0 : 3}
          backgroundColor={"surface-primary"}
          marginRight={shouldShowBookingPayment ? 3 : "auto"}
        >
          <BookingSummaryLayout
            header={
              <BookingHeader
                clubRef={
                  data.session?.currentClientProfile?.member?.club ?? null
                }
                bookingRef={booking ?? null}
                handleGoBack={handleGoBack}
              />
            }
            body={
              <>
                <BookingInfo
                  bookingRef={booking ?? null}
                  forceRequestState={shouldPresentAsRequest}
                  hideStartTime={shouldPresentAsRequest}
                  recurrenceAvailabilityPreview={recurrenceAvailabilityPreview}
                  pendingRecurrence={pendingRecurrence}
                >
                  <>
                    {isClosedRequestWindow ? (
                      <ClosedRequestWindowBanner
                        clubPhoneNumber={
                          booking?.startSlot?.agenda?.facility?.club
                            ?.phoneNumber?.formattedNumber ?? undefined
                        }
                      />
                    ) : (
                      <RequestInfoBanner
                        bookingRef={booking ?? null}
                        isRecurringSeries={!!pendingRecurrence}
                      />
                    )}
                    <LessonInfoBanner bookingRef={booking ?? null} />
                    <RemoteLockCode
                      bookingRef={booking ?? null}
                      fragmentRef={booking?.startSlot?.agenda?.facility ?? null}
                    />
                    <MemberNote bookingRef={booking ?? null} />
                    <AvailableTeeTimes
                      bookingId={booking?.id ?? ""}
                      bookingFragmentRef={booking ?? null}
                      bookingType={booking?.__typename}
                      hasAvailableMultipleSlotRequestSequence={
                        hasAvailableMultipleSlotRequestSequence
                      }
                      requestGroupCount={effectiveRequestGroupCount}
                    />
                  </>
                </BookingInfo>

                <InviteOthersBox
                  bookingRef={booking ?? null}
                  clubSlug={clubSlug}
                />
                <BookingDetailsView
                  bookingRef={booking ?? null}
                  currentSessionMemberShipTypeId={
                    currentSessionMemberShipTypeId
                  }
                  currentSessionUserClientId={currentSessionUserClientId}
                  onRecurrencePreviewChange={setRecurrenceAvailabilityPreview}
                  requestGroupCount={effectiveRequestGroupCount}
                  requestRoundBookingIndexes={requestRoundBookingIndexes}
                />
                {shouldShowChargesSummary ? (
                  <ChargesSummary
                    bookingRef={booking ?? null}
                    isBookingOwner={!!isBookingOwner}
                  />
                ) : null}
                <BookingPolicy bookingRef={booking ?? null} />
              </>
            }
            footer={
              shouldShowFooter ? (
                <BookingReservation
                  bookingFragmentRef={booking ?? null}
                  currentServerTime={data.currentServerTime}
                  handleGoBack={handleGoBack}
                  isMemberCaller={isMemberCaller}
                  membershipTypeId={currentSessionMemberShipTypeId}
                  hasPublicSeenNotificationNudgeModal={
                    hasPublicSeenNotificationNudgeModal
                  }
                  recurrenceAvailabilityPreview={recurrenceAvailabilityPreview}
                  isBookingOwner={!!isBookingOwner}
                  requestGroupCount={effectiveRequestGroupCount}
                />
              ) : null
            }
          />
        </Box>
      </Box>

      {shouldShowBookingPayment ? (
        <BookingPayment
          handleGoBack={handleGoBack}
          bookingFragmentRef={booking ?? null}
          isMemberCaller={isMemberCaller}
          currentServerTime={data.currentServerTime}
          membershipTypeId={currentSessionMemberShipTypeId}
          hasPublicSeenNotificationNudgeModal={
            hasPublicSeenNotificationNudgeModal
          }
          isBookingOwner={!!isBookingOwner}
          requestGroupCount={effectiveRequestGroupCount}
        />
      ) : null}
      {!isStacked ? (
        <Box
          backgroundColor={"surface-secondary"}
          flexGrow={1}
          flexShrink={1}
          flexBasis={0}
        />
      ) : null}
      {session && booking ? (
        <PaymentCardEntryModals bookingRef={booking} />
      ) : null}
      {session && booking ? (
        // Houses gift cards and rain checks modals
        <BookingCreditSelectModals bookingRef={booking} />
      ) : null}
      {hasPublicSeenNotificationNudgeModal ? null : (
        <NotificationsNudgeModal
          userId={data.session?.user?.id ?? ""}
          isOpen={isNotificationsNudgeModalOpen}
          setNudgeModal={setIsNotificationsNudgeModalOpen}
          isNewFlow={true}
        />
      )}

      {isConfirmedBookingModalOpen && booking ? (
        <BookingConfirmedModal
          isOpen={isConfirmedBookingModalOpen}
          setConfirmedBookingModal={setIsConfirmedBookingModalOpen}
          bookingRef={booking}
          type={confirmedBookingModalType}
        />
      ) : null}
    </HStack>
  );
};

const BookingSummarySessionQueryString = graphql`
  query BookingSummarySessionQuery {
    session {
      ...BookingSummaryMultipleViewSession_data
      ...useCurrentUserHasSeenNotificationNudgeModal_session
      currentClientProfile {
        ... on MemberProfile {
          member {
            __typename
            membershipType {
              id
            }
            ... on Member {
              id
            }
            ... on Customer {
              id
            }
            club {
              supportsPrivate
            }
          }
        }
      }
    }
  }
`;

/**
 * This is a fragment so that it is more portable -- and easy to stick into things
 * like mutations -- so that we can be sure we update our entire screen
 */
const BookingSummaryBookingFragmentString = graphql`
  fragment BookingSummary_booking on Booking
  @argumentDefinitions(
    patronLoyalty: { type: "Boolean!", defaultValue: false }
  ) {
    id
    __typename
    ...useGetBookingAnalyticsMetadata_booking
    ...RemoteLockCode_Booking
    ...ChargesSummary_booking @arguments(patronLoyalty: $patronLoyalty)
    ...PaymentCardEntryModals_booking
    ...BookingPayment_Booking @arguments(patronLoyalty: $patronLoyalty)
    ...InviteOthersBox_Booking
    ...BookingInfo_Booking
    ...BookingDetailsView_Booking
    ...BookingPolicy_Booking
    ...BookingPaymentMethodSelect_booking
    ...BookingReservation_Booking @arguments(patronLoyalty: $patronLoyalty)
    ...BookingHeaderFragment_booking
    ...MemberNote_Booking
    ...RequestInfoBanner_booking
    ...LessonInfoBanner_booking
    ...AvailableTeeTimes_booking
    ...useShouldPresentBookingAsLockedRequest_booking
    ...BookingCreditSelectModals_booking
    ...useGetFullfillableSlots_booking
    ... on StandardBooking {
      status
      restrictionStatuses {
        groupStatuses {
          statuses {
            restriction {
              name
            }
          }
        }
        roundStatuses {
          statuses {
            restriction {
              name
            }
          }
        }
      }
      ...useInitializePunchPassesFromBooking
    }
    ... on LessonBooking {
      status
      restrictionStatuses {
        groupStatuses {
          statuses {
            restriction {
              name
            }
          }
        }
        roundStatuses {
          statuses {
            restriction {
              name
            }
          }
        }
      }
      ...useInitializePunchPassesFromBooking
    }
    ownerClient {
      ... on Customer {
        id
      }
      ... on Member {
        id
      }
    }
    startSlot {
      __typename
      id
      agenda {
        facility {
          club {
            supportsPrivate
            phoneNumber {
              formattedNumber
            }
          }
          privateLessonsPermissionSet {
            patronAccess
          }
          ...RemoteLockCode_BookingSummaryRemoteLockCodeConfig
        }
      }
    }
    ...BookingConfirmedModal_Booking
  }
`;

const BookingSummaryQueryString = graphql`
  query BookingSummaryQuery(
    $bookingId: ID!
    $membershipTypeId: ID!
    $bookingType: BookingType!
    $patronLoyalty: Boolean!
  ) {
    currentServerTime
    session {
      ...useCurrentUserHasSeenNotificationNudgeModal_session
      ...BookingSummaryMultipleViewSession_data
      currentClientProfile {
        member {
          __typename
          ... on Member {
            id
          }
          ... on Customer {
            id
            squareGiftCards(hasNonZeroBalance: true) {
              edges {
                node {
                  id
                }
              }
            }
          }
          club {
            slug
            ...BookingHeaderFragment_BookingSummary
          }
        }
      }
      user {
        id
      }
    }
    node(id: $bookingId) {
      ... on Booking {
        ...BookingSummary_booking @arguments(patronLoyalty: $patronLoyalty)
        rounds {
          edges {
            node {
              bookingIndex
            }
          }
        }
      }
      ... on StandardBooking {
        hasFullfillableMultipleSlotRequestSequence
      }
    }
  }
`;
